import { useQuery } from "@apollo/client";
import { Typography, styled } from "@mui/material";
import { ReactElement, useEffect, useRef } from "react";

import {
  GetSubaccountAdminFieldsQuery,
  GetSubaccountAdminFieldsQueryVariables,
  ListResponseScriptsQuery,
  ListResponseScriptsQueryVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { ResponseScriptAddButtons } from "~frontend/components/responseScripts/ResponseScriptAddButtons";
import { ResponseScriptList } from "~frontend/components/responseScripts/ResponseScriptList";
import { LIST_RESPONSE_SCRIPT_GROUPS } from "~frontend/graphql/scripts";
import { GET_SUBACCOUNT_ADMIN_FIELDS } from "~frontend/graphql/subaccounts";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { SubaccountAdminData } from "~frontend/providers/LoggedInUserProvider/context";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { jobInProgress } from "~frontend/util/jobs";

export const IntroText = styled(
  "div",
  {}
)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: 0,
  minWidth: "100%",
}));

function responseScriptImportToSubaccountJob(subaccount: SubaccountAdminData) {
  if (!subaccount.responseScriptImportToSubaccountJob) {
    return false;
  }

  return jobInProgress(subaccount.responseScriptImportToSubaccountJob);
}

export function SubaccountResponseScripts(): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  const subaccount = useSubaccountAdminConfig()!;
  const intl = useIntl();
  useBreadcrumbs([
    {
      label: intl.formatMessage({
        defaultMessage: "Response Scripts",
        description: "Title of the Response Scripts page",
      }),
      path: subaccountLink("/scripts"),
    },
  ]);

  const nonCacheClearingClient = useNonCacheClearingApolloClient();

  const { startPolling, stopPolling } = useQuery<
    GetSubaccountAdminFieldsQuery,
    GetSubaccountAdminFieldsQueryVariables
  >(GET_SUBACCOUNT_ADMIN_FIELDS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
    client: nonCacheClearingClient,
  });

  const groupsResult = useQuery<
    ListResponseScriptsQuery,
    ListResponseScriptsQueryVariables
  >(LIST_RESPONSE_SCRIPT_GROUPS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
    pollInterval: 60 * 1000,
  });

  const jobWasInProgress = useRef(
    responseScriptImportToSubaccountJob(subaccount)
  );
  useEffect(() => {
    const currentStatus = responseScriptImportToSubaccountJob(subaccount);
    if (jobWasInProgress.current !== currentStatus) {
      if (currentStatus) {
        startPolling(1000);
      } else {
        stopPolling();
        if (jobWasInProgress.current) {
          void groupsResult.refetch();
        }
      }
    }
    jobWasInProgress.current = currentStatus;
  }, [jobWasInProgress, subaccount, startPolling, stopPolling, groupsResult]);

  if (groupsResult.error) {
    return <ErrorCard errors={[groupsResult.error]} />;
  }

  return (
    <PageContainer maxWidth="lg">
      <PageHeader isSlim>Response Scripts</PageHeader>
      <Typography variant="h2">
        Write scripted responses for your texters to choose from when replying
        to contacts. You’ll be able to select which responses and response
        groups texters are shown on a per campaign basis when building your
        campaign.
      </Typography>

      <ResponseScriptAddButtons
        groups={groupsResult.data?.listResponseScripts || []}
        subaccount={subaccount}
      />
      <ResponseScriptList
        loading={groupsResult.loading}
        groups={groupsResult.data?.listResponseScripts || []}
        sx={{
          backgroundColor: "white",
        }}
      />
    </PageContainer>
  );
}
